import { useState } from 'react';
import { CabeceraPagina } from '../../components/Armazon';
import { Aviso, Boton, Campo, Cargando, Dialogo, PildoraLibre, Tarjeta, Vacio } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { ROL_ETIQUETA, fechaHora } from '../../lib/formato';
import { useAvisos, useDatos } from '../../lib/sesion';
import type { Rol } from '../../lib/tipos';

interface UsuarioFila {
  id: string;
  name: string;
  email: string;
  role: Rol;
  active: boolean;
  googleEmail: string | null;
  googleCalendarId: string | null;
  lastLoginAt: string | null;
  mustChangePassword: boolean;
  salesperson: { id: string; externalId: string; name: string | null } | null;
}

interface Comercial {
  id: string;
  externalId: string;
  name: string | null;
}

const ROLES: Rol[] = ['SALESPERSON', 'SALES_MANAGER', 'COOK', 'ADMIN'];

export function Usuarios() {
  const { avisar } = useAvisos();
  const usuarios = useDatos((s) => api.get<{ rows: UsuarioFila[] }>('/admin/users', s), []);
  const comerciales = useDatos((s) => api.get<{ rows: Comercial[] }>('/salespeople', s), []);
  const [editando, setEditando] = useState<UsuarioFila | 'nuevo' | null>(null);
  const [claveTemporal, setClaveTemporal] = useState<{ nombre: string; valor: string } | null>(null);
  const [copiada, setCopiada] = useState(false);

  return (
    <>
      <CabeceraPagina
        titulo="Usuarios"
        acciones={
          <Boton variante="clave" onClick={() => setEditando('nuevo')}>
            Nuevo usuario
          </Boton>
        }
      />

      {usuarios.error && <Aviso tono="error">{usuarios.error}</Aviso>}

      <Tarjeta sinRelleno>
        {usuarios.cargando ? (
          <Cargando />
        ) : usuarios.datos?.rows.length ? (
          <table className="tabla">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Perfil</th>
                <th>Id. vendedor</th>
                <th>Último acceso</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {usuarios.datos.rows.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{u.name}</div>
                    <div className="sutil">{u.email}</div>
                  </td>
                  <td className="sutil">{ROL_ETIQUETA[u.role]}</td>
                  <td className="codigo">{u.salesperson?.externalId ?? '—'}</td>
                  <td className="sutil">{u.lastLoginAt ? fechaHora(u.lastLoginAt) : 'Nunca'}</td>
                  <td>
                    <PildoraLibre
                      texto={u.active ? 'Activo' : 'Desactivado'}
                      tono={u.active ? 'hecho' : 'alto'}
                    />
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Boton variante="plano" tam="s" onClick={() => setEditando(u)}>
                      Editar
                    </Boton>
                    <Boton
                      variante="plano"
                      tam="s"
                      onClick={async () => {
                        await api.post(`/admin/users/${u.id}/active`, { active: !u.active });
                        avisar(u.active ? 'Usuario desactivado.' : 'Usuario activado.', 'hecho');
                        usuarios.recargar();
                      }}
                    >
                      {u.active ? 'Desactivar' : 'Activar'}
                    </Boton>
                    <Boton
                      variante="plano"
                      tam="s"
                      onClick={async () => {
                        const r = await api.post<{ temporaryPassword: string }>(
                          `/admin/users/${u.id}/reset-password`,
                        );
                        setCopiada(false);
                        setClaveTemporal({ nombre: u.name, valor: r.temporaryPassword });
                        usuarios.recargar();
                      }}
                    >
                      Restablecer clave
                    </Boton>
                    <Boton
                      variante="plano"
                      tam="s"
                      disabled={!u.active}
                      onClick={async () => {
                        await api.post(`/admin/users/${u.id}/send-access`, {});
                        avisar(`Enlace de acceso enviado a ${u.email}.`, 'hecho');
                      }}
                    >
                      Enviar acceso por correo
                    </Boton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Vacio titulo="Sin usuarios" />
        )}
      </Tarjeta>

      {editando && (
        <FormularioUsuario
          usuario={editando === 'nuevo' ? null : editando}
          comerciales={comerciales.datos?.rows ?? []}
          onCerrar={() => setEditando(null)}
          onHecho={({ mensaje, temporaryPassword, nombre }) => {
            avisar(mensaje, 'hecho');
            setEditando(null);
            if (temporaryPassword) {
              setCopiada(false);
              setClaveTemporal({ nombre, valor: temporaryPassword });
            }
            usuarios.recargar();
          }}
        />
      )}

      {claveTemporal && (
        <Dialogo
          titulo="Contraseña temporal"
          descripcion={`Contraseña temporal de ${claveTemporal.nombre}. Solo se muestra esta vez.`}
          onCerrar={() => setClaveTemporal(null)}
          acciones={
            <>
              <Boton
                variante="secundario"
                onClick={async () => {
                  await navigator.clipboard.writeText(claveTemporal.valor);
                  setCopiada(true);
                }}
              >
                {copiada ? 'Copiada' : 'Copiar'}
              </Boton>
              <Boton variante="clave" onClick={() => setClaveTemporal(null)}>
                Cerrar
              </Boton>
            </>
          }
        >
          <Aviso tono="aviso">
            Entréguela de forma segura. El usuario tendrá que cambiarla en su próximo acceso.
          </Aviso>
          <Campo etiqueta="Contraseña temporal">
            <input type="text" readOnly value={claveTemporal.valor} />
          </Campo>
        </Dialogo>
      )}
    </>
  );
}

function FormularioUsuario({
  usuario,
  comerciales,
  onCerrar,
  onHecho,
}: {
  usuario: UsuarioFila | null;
  comerciales: Comercial[];
  onCerrar: () => void;
  onHecho: (resultado: { mensaje: string; temporaryPassword?: string; nombre: string }) => void;
}) {
  const [form, setForm] = useState({
    name: usuario?.name ?? '',
    email: usuario?.email ?? '',
    role: usuario?.role ?? ('SALESPERSON' as Rol),
    salespersonId: usuario?.salesperson?.id ?? '',
    googleEmail: usuario?.googleEmail ?? '',
    googleCalendarId: usuario?.googleCalendarId ?? '',
    password: '',
    mustChangePassword: true,
  });
  const [fallo, setFallo] = useState<string | null>(null);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);

  async function guardar() {
    setGuardando(true);
    setFallo(null);
    setErrores({});
    const cuerpo: Record<string, unknown> = {
      name: form.name.trim(),
      email: form.email.trim(),
      role: form.role,
      salespersonId: form.role === 'SALESPERSON' ? form.salespersonId || null : null,
      googleEmail: form.googleEmail.trim() || null,
      googleCalendarId: form.googleCalendarId.trim() || null,
    };
    if (form.password) cuerpo.password = form.password;
    if (usuario && form.password) cuerpo.mustChangePassword = form.mustChangePassword;

    try {
      if (usuario) {
        const result = await api.patch<{ warning?: string }>(`/admin/users/${usuario.id}`, cuerpo);
        onHecho({ mensaje: result.warning ?? 'Usuario actualizado.', nombre: form.name.trim() });
      } else {
        const r = await api.post<{ temporaryPassword?: string }>('/admin/users', cuerpo);
        onHecho({
          mensaje: 'Usuario creado.',
          temporaryPassword: r.temporaryPassword,
          nombre: form.name.trim(),
        });
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setFallo(e.message);
        setErrores(e.fieldErrors);
      } else setFallo('No se ha podido guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialogo
      titulo={usuario ? 'Editar usuario' : 'Nuevo usuario'}
      descripcion="Los Comerciales solo ven los clientes de su Id. de vendedor."
      onCerrar={onCerrar}
      acciones={
        <>
          <Boton variante="secundario" onClick={onCerrar}>
            Volver
          </Boton>
          <Boton variante="clave" cargando={guardando} onClick={guardar}>
            {usuario ? 'Guardar cambios' : 'Crear usuario'}
          </Boton>
        </>
      }
    >
      {fallo && <Aviso tono="error">{fallo}</Aviso>}

      <Campo etiqueta="Nombre y apellidos" error={errores.name}>
        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </Campo>
      <Campo etiqueta="Correo de empresa" error={errores.email}>
        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </Campo>
      <Campo etiqueta="Perfil" error={errores.role}>
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Rol })}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ROL_ETIQUETA[r]}
            </option>
          ))}
        </select>
      </Campo>

      {form.role === 'SALESPERSON' && (
        <Campo
          etiqueta="Id. de vendedor"
          error={errores.salespersonId}
        >
          <select
            value={form.salespersonId}
            onChange={(e) => setForm({ ...form, salespersonId: e.target.value })}
          >
            <option value="">Sin asignar</option>
            {comerciales.map((c) => (
              <option key={c.id} value={c.id}>
                {c.externalId} · {c.name ?? 'Sin nombre'}
              </option>
            ))}
          </select>
        </Campo>
      )}

      <Campo etiqueta="Calendario de Google" ayuda="Identificador del calendario propio, si lo tiene.">
        <input
          type="text"
          value={form.googleCalendarId}
          onChange={(e) => setForm({ ...form, googleCalendarId: e.target.value })}
        />
      </Campo>

      <Campo
        etiqueta={usuario ? 'Nueva contraseña (opcional)' : 'Contraseña inicial (opcional)'}
        ayuda="Si se deja en blanco, se genera una temporal y se muestra una sola vez."
        error={errores.password}
      >
        <input
          type="password"
          autoComplete="new-password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
      </Campo>
      {usuario && form.password && (
        <label className="casilla">
          <input
            type="checkbox"
            checked={form.mustChangePassword}
            onChange={(e) => setForm({ ...form, mustChangePassword: e.target.checked })}
          />
          Obligar a cambiarla en el próximo acceso
        </label>
      )}
    </Dialogo>
  );
}
